import { createContext, useContext, useEffect, useState, Fragment } from "react";
import api, { apiErr, fmt2 } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { fl, srcLabel } from "@/lib/finI18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TransactionDetailPanel } from "@/components/TransactionDetailPanel";
import { ChevronRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const DrillCtx = createContext(null);
export const useDrill = () => useContext(DrillCtx);
const n = (v) => fmt2(v || 0);

export function DrillNum({ value, q, title, testid, className = "", dash }) {
  const drill = useDrill();
  const txt = dash && !value ? "—" : n(value);
  if (!drill || !q) return <span className={`num ${className}`}>{txt}</span>;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); drill({ value, ...q, title }); }} data-testid={testid}
      className={`num cursor-pointer decoration-dotted underline-offset-2 transition-colors hover:text-primary hover:underline ${className}`}>{txt}</button>
  );
}

export function DrillProvider({ base, lang, children }) {
  const [q, setQ] = useState(null);
  return (
    <DrillCtx.Provider value={setQ}>
      {children}
      <DrillDialog q={q} base={base} lang={lang} onClose={() => setQ(null)} />
    </DrillCtx.Provider>
  );
}

function DocJournal({ lines, lang }) {
  return (
    <div className="rounded-md border border-border bg-background p-2" data-testid="drill-doc-journal">
      <p className="text-[11px] font-semibold text-muted-foreground mb-1">{fl("journalOfDoc", lang)}</p>
      {(lines || []).map((x, i) => (
        <div key={i} className="grid grid-cols-[1fr_120px_120px] gap-2 text-xs py-0.5">
          <span>{x.account_code} {x.account_name}{x.party ? <span className="text-muted-foreground"> · {x.party}</span> : null}</span>
          <span className="num text-right">{x.debit ? n(x.debit) : "—"}</span><span className="num text-right">{x.credit ? n(x.credit) : "—"}</span>
        </div>
      ))}
    </div>
  );
}

function DrillRow({ l, i, open, onToggle, docs, lang, onOpenTxn }) {
  return (
    <Fragment>
      <TableRow className="cursor-pointer hover:bg-secondary/50" onClick={onToggle} data-testid={`drill-line-${i}`}>
        <TableCell className="text-xs whitespace-nowrap"><ChevronRight className={`inline h-3 w-3 mr-1 transition-transform ${open ? "rotate-90" : ""}`} />{fmtDate(l.date)}</TableCell>
        <TableCell className="text-xs">{l.doc_no || "—"}</TableCell>
        <TableCell><Badge variant="outline" className="text-[10px] whitespace-nowrap">{srcLabel(l.source, lang)}</Badge></TableCell>
        <TableCell className="text-xs">{l.party || "—"}</TableCell>
        <TableCell className="text-xs whitespace-nowrap">{l.account_code} {l.account_name}</TableCell>
        <TableCell className="text-xs max-w-[240px]">{l.description}</TableCell>
        <TableCell className="text-[11px] text-muted-foreground max-w-[200px]">{l.counter || "—"}</TableCell>
        <TableCell className="num text-right text-xs">{l.debit ? n(l.debit) : "—"}</TableCell>
        <TableCell className="num text-right text-xs">{l.credit ? n(l.credit) : "—"}</TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-secondary/30">
          <TableCell colSpan={9} className="py-2">
            <div className="flex flex-col gap-2">
              <DocJournal lines={docs[l.doc_id]} lang={lang} />
              {l.txn_id && <Button size="sm" variant="outline" className="self-start h-7" onClick={() => onOpenTxn(l.txn_id)} data-testid={`drill-open-txn-${i}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />{fl("openTxn", lang)}</Button>}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function DrillDialog({ q, base, lang, onClose }) {
  const [data, setData] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [txnId, setTxnId] = useState(null);
  useEffect(() => {
    setData(null); setOpenIdx(null);
    if (!q) return;
    const p = new URLSearchParams(Object.fromEntries(Object.entries(base).filter(([, v]) => v)));
    p.set("basis", q.basis || "period");
    ["account", "party", "doc", "types"].forEach((k) => { if (q[k] != null && q[k] !== "") p.set(k, q[k]); });
    api.get(`/reports/drill?${p}`).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [q, base]);
  return (
    <>
      <Dialog open={!!q} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-head" data-testid="drill-title">{q?.title || fl("drillTitle", lang)}</DialogTitle>
            <DialogDescription className="flex flex-wrap gap-3 text-xs" data-testid="drill-summary">
              {data ? <>{q?.value != null && <span className="font-semibold text-foreground" data-testid="drill-reported">{fl("reported", lang)} {n(q.value)}</span>}<span>{data.count} {fl("entries", lang)}</span><span>{fl("debit", lang)} {n(data.total_debit)}</span><span>{fl("credit", lang)} {n(data.total_credit)}</span><span className="font-semibold" data-testid="drill-net">{fl("net", lang)} {n(q?.normal === "credit" ? -data.net : data.net)}</span>{data.truncated && <span className="text-amber-600">{fl("truncated", lang)}</span>}</> : fl("loading", lang)}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto -mx-2 px-2">
            {data && (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("docNo", lang)}</TableHead><TableHead>{fl("source", lang)}</TableHead><TableHead>{fl("ledger", lang)}</TableHead>
                  <TableHead>{fl("account", lang)}</TableHead><TableHead>{fl("desc", lang)}</TableHead><TableHead>{fl("counter", lang)}</TableHead>
                  <TableHead className="text-right">{fl("debit", lang)}</TableHead><TableHead className="text-right">{fl("credit", lang)}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.lines.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-muted-foreground">{fl("empty", lang)}</TableCell></TableRow>}
                  {data.lines.map((l, i) => <DrillRow key={i} l={l} i={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? null : i)} docs={data.docs} lang={lang} onOpenTxn={setTxnId} />)}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <TransactionDetailPanel txnId={txnId} open={!!txnId} onOpenChange={(o) => { if (!o) setTxnId(null); }} />
    </>
  );
}
